export namespace NumberUtils {
  export const CPI_INFLATION_RATE = 0.02;
  const NAN_VALUE = "-";

  export const formatAsUSCurrency = (
    value: number,
    options?: {
      maximumDecimalPlaces?: number;
      maximumSignificantDigits?: number;
      useAbsoluteValue?: boolean;
      hideCurrency?: boolean;
    },
  ): string => {
    if (Number.isNaN(value)) {
      return NAN_VALUE;
    }
    const valueToFormat = options?.useAbsoluteValue ? Math.abs(value) : value;
    const valueFormatter = new Intl.NumberFormat("en-US", {
      ...(options?.hideCurrency ? { style: "decimal" } : { style: "currency", currency: "USD" }),
      maximumFractionDigits: options?.maximumDecimalPlaces ?? 0,
      maximumSignificantDigits: options?.maximumSignificantDigits,
    });
    return valueFormatter.format(valueToFormat);
  };

  export const formatNumberToShort = (value: number, useAbsoluteValue = false) => {
    const absValue = useAbsoluteValue ? Math.abs(value) : value;
    if (absValue >= 1000000) {
      return `${(absValue / 1000000).toFixed(1)}M`;
    }
    if (absValue >= 1000) {
      return `${(absValue / 1000).toFixed(0)}K`;
    }
    return `${absValue}`;
  };

  /**
   * Formats numbers to short format (K/M) without currency symbol
   * Useful for chart axes and labels
   * @param value - The number to format
   * @returns Formatted string (e.g., "5K", "1.5M", "2M")
   */
  export const formatNumberToShortNoCurrency = (value: number): string => {
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000) {
      const millions = absValue / 1_000_000;
      // Show decimals for values like 1.5M, but not for whole millions like 2M
      return millions % 1 === 0 ? `${millions.toFixed(0)}M` : `${millions.toFixed(1)}M`;
    }
    if (absValue >= 1_000) {
      return `${(absValue / 1_000).toFixed(0)}K`;
    }
    return `${absValue}`;
  };

  export const formatSalary = (
    value: number,
    options?: { maximumDecimalPlaces?: number; useAbsoluteValue?: boolean },
  ): string => {
    if (Number.isNaN(value)) {
      return NAN_VALUE;
    }
    const valueToFormat = options?.useAbsoluteValue ? Math.abs(value) : value;
    const valueFormatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: options?.maximumDecimalPlaces ?? 2,
    });
    return valueFormatter.format(valueToFormat);
  };

  /**
   * Converts a formatted salary string to a number
   * Handles strings like "123,123.456" -> 123123.456
   * @param value - The salary value (string or number)
   * @returns The numeric value or null if invalid
   */
  export const parseSalaryString = (value: string | number | null | undefined): number | null => {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === "number") {
      return Number.isNaN(value) ? null : value;
    }

    if (typeof value === "string") {
      // Remove all non-numeric characters except decimal point
      const cleanedValue = value.replace(/[^0-9.]/g, "");

      // Handle empty string
      if (cleanedValue === "" || cleanedValue === ".") {
        return null;
      }

      // Parse the cleaned value
      const numericValue = Number(cleanedValue);
      return Number.isNaN(numericValue) ? null : numericValue;
    }

    return null;
  };

  /**
   * Formats a phone number string with proper formatting
   * @param digits - The phone number digits (without formatting)
   * @returns The formatted phone number string
   */
  export const formatPhoneNumber = (digits: string, allowLongerThan10Digits = false): string => {
    const cleaned = digits.replace(/\D/g, "");

    if (cleaned === "") {
      return "";
    }

    // US default for 10-digit national numbers
    if (cleaned.length === 10) {
      const area = cleaned.slice(0, 3);
      const exchange = cleaned.slice(3, 6);
      const line = cleaned.slice(6);
      return `${area} ${exchange} ${line}`;
    }

    // US with leading 1
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      const area = cleaned.slice(1, 4);
      const exchange = cleaned.slice(4, 7);
      const line = cleaned.slice(7);
      return `+1 ${area} ${exchange} ${line}`;
    }

    if (allowLongerThan10Digits && cleaned.length > 10) {
      // Generic fallback: just prefix + and add a space after country code guess (first 1–3 digits)
      const country = cleaned.slice(0, 3);
      const rest = cleaned.slice(3);
      return `+${country} ${rest}`;
    }

    return cleaned;
  };

  export const formatPoints = (points: number): string => {
    return new Intl.NumberFormat("en-US").format(points);
  };
}
